import { useEffect, useMemo, useState } from "react";

import {
  dispatchStatisticsFetchError,
  normalizeStatisticsFetchError,
  STATISTICS_FETCH_ERROR_EVENT,
  type StatisticsFetchError,
  type StatisticsFetchErrorEventDetail,
} from "../utils/statisticsFetchErrors";

export type { StatisticsFetchError };

export function useStatisticsFetchErrorState(
  cacheKey: string,
  fallbackMessage: string
): {
  error: string | null;
  errorAction: string | null;
  errorSlug: string | null;
  errorStatus: number | null;
  setFetchError: (err: unknown | null) => void;
  clearFetchError: () => void;
} {
  const [fetchError, setFetchErrorState] = useState<StatisticsFetchError | null>(null);

  const setFetchError = (err: unknown | null) => {
    if (err == null) {
      setFetchErrorState(null);
      dispatchStatisticsFetchError(cacheKey, null);
      return;
    }
    const normalized = normalizeStatisticsFetchError(err, fallbackMessage);
    setFetchErrorState(normalized);
    dispatchStatisticsFetchError(cacheKey, normalized);
  };

  const clearFetchError = () => setFetchError(null);

  return {
    error: fetchError?.message ?? null,
    errorAction: fetchError?.action ?? null,
    errorSlug: fetchError?.slug ?? null,
    errorStatus: fetchError?.status ?? null,
    setFetchError,
    clearFetchError,
  };
}

/** Collect parallel statistics endpoint failures for page-level aggregation. */
export function useStatisticsFetchErrorCollector(): Map<string, StatisticsFetchError | null> {
  const [failures, setFailures] = useState<Map<string, StatisticsFetchError | null>>(new Map());

  useEffect(() => {
    const onError = (event: Event) => {
      const detail = (event as CustomEvent<StatisticsFetchErrorEventDetail>).detail;
      if (!detail?.key) return;
      setFailures((prev) => {
        const next = new Map(prev);
        if (!detail.error) next.delete(detail.key);
        else next.set(detail.key, detail.error);
        return next;
      });
    };
    window.addEventListener(STATISTICS_FETCH_ERROR_EVENT, onError);
    return () => window.removeEventListener(STATISTICS_FETCH_ERROR_EVENT, onError);
  }, []);

  return failures;
}

export function statisticsFailureSources(
  failures: Map<string, StatisticsFetchError | null>
): Array<{ key: string; error: string | null; action?: string; slug?: string; status?: number }> {
  return Array.from(failures.entries()).map(([key, value]) => ({
    key,
    error: value?.message ?? null,
    action: value?.action,
    slug: value?.slug,
    status: value?.status,
  }));
}

export function useStatisticsPageErrors(options?: {
  headlineKey?: string;
  defaultMessage?: string;
}) {
  const failures = useStatisticsFetchErrorCollector();
  const sources = useMemo(() => statisticsFailureSources(failures), [failures]);
  const failedCount = sources.filter((s) => s.error).length;
  const headlineKey = options?.headlineKey ?? "statistics";
  const defaultMessage =
    options?.defaultMessage ?? "Couldn't load statistics. Refresh or try again.";

  return useMemo(() => {
    if (failedCount < 2) {
      return { pageError: null, pageErrorAction: null, suppressWidgetErrors: false, failedCount };
    }
    const headline = sources.find((s) => s.key.startsWith(headlineKey) && s.error);
    const fallback = sources.find((s) => s.error);
    const pageError = headline?.error || fallback?.error || defaultMessage;
    const pageErrorAction = headline?.action || fallback?.action || null;
    return { pageError, pageErrorAction, suppressWidgetErrors: true, failedCount };
  }, [sources, failedCount, headlineKey, defaultMessage]);
}
